import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  withStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';

import Checkbox from '~/components/Checkbox';
import AsyncAutocomplete from '~/components/AsyncAutocomplete';

import { create } from '../../../feathersWrapper';

const styles = () => ({
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    padding: '16px',
    marginTop: '16px',
    marginBottom: '16px',
  },
  errorBox: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '16px',
    marginTop: '16px',
  },
  successBox: {
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '16px',
  },
  nextStepsBox: {
    backgroundColor: '#e2e3e5',
    border: '1px solid #d6d8db',
    borderRadius: '4px',
    padding: '16px',
    marginTop: '16px',
  },
  stepItem: {
    marginBottom: '12px',
  },
});

class MergePortfolioDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      sourcePortfolio: null,
      destinationPortfolio: null,
      confirmed: false,
      loading: false,
      success: false,
      error: null,
      mergeResult: null,
    };
  }

  handleMerge = async () => {
    const { sourcePortfolio, destinationPortfolio } = this.state;

    this.setState({ loading: true, error: null });

    try {
      const result = await create(
        this,
        'portfolio-merge',
        {
          sourceId: sourcePortfolio.id,
          destinationId: destinationPortfolio.id,
        },
        true,
      );

      this.setState({
        loading: false,
        success: true,
        mergeResult: result,
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: err.message || 'An error occurred while merging portfolios',
      });
    }
  };

  handleClose = () => {
    const { closeDialog } = this.props;
    this.setState({
      sourcePortfolio: null,
      destinationPortfolio: null,
      confirmed: false,
      loading: false,
      success: false,
      error: null,
      mergeResult: null,
    });
    closeDialog();
  };

  renderSuccessView = () => {
    const { classes } = this.props;
    const { mergeResult } = this.state;

    return (
      <>
        <Box className={classes.successBox}>
          <Typography variant="h6" gutterBottom>
            Portfolio Merge Completed Successfully
          </Typography>
          <Typography variant="body2">
            {`Merged "${mergeResult.sourceName}" (ID: ${mergeResult.sourceId}) into "${mergeResult.destinationName}" (ID: ${mergeResult.destinationId})`}
          </Typography>
        </Box>

        <Box className={classes.nextStepsBox}>
          <Typography variant="h6" gutterBottom>
            Next Steps (Manual Actions Required)
          </Typography>

          <Box className={classes.stepItem}>
            <Typography variant="body2">
              <strong>1. Review Merged Properties:</strong> All properties from the source portfolio
              have been tagged with &apos;-m&apos; at the end of their address for easy identification.
              These may be duplicates of properties that already existed in the destination portfolio.
            </Typography>
          </Box>

          <Box className={classes.stepItem}>
            <Typography variant="body2">
              <strong>2. Delete Duplicate Properties:</strong> Delete all duplicate properties
              (those tagged with &apos;-m&apos;) through the frontend. The system will prompt you to
              select a new transaction scope if the item has booked activity. Individual units must
              be deleted before the parent property to preserve unit level info. Edit merged
              properties to remove &apos;-m&apos; if they are not duplicates.
            </Typography>
          </Box>

          <Box className={classes.stepItem}>
            <Typography variant="body2">
              <strong>3. Review Linked Accounts:</strong> Adjust the transaction import start dates
              and delete/merge any duplicate linked accounts through the frontend if necessary.
            </Typography>
          </Box>

          <Box className={classes.stepItem}>
            <Typography variant="body2">
              <strong>4. Cancel Source Subscription:</strong> Through the frontend, cancel the
              subscription of the source portfolio. Issue a full refund through the Stripe dashboard
              if it was a brand new subscription. Issue a prorated refund if they were on annual
              billing with substantial time remaining.
            </Typography>
          </Box>

          <Box className={classes.stepItem}>
            <Typography variant="body2">
              <strong>5. Notify Stakeholders:</strong> Notify CX and/or the customer about the
              completed merge.
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  renderMergeForm = () => {
    const { classes } = this.props;
    const { sourcePortfolio, destinationPortfolio, confirmed, error } = this.state;

    return (
      <>
        <Box mb={2}>
          <Typography variant="body2" gutterBottom>
            Select the source portfolio (will be merged INTO the destination):
          </Typography>
          <AsyncAutocomplete
            type="portfolio"
            onAddValue={(value) => {
              this.setState({ sourcePortfolio: value ? { id: value.id, label: value.label } : null });
            }}
            label="Source Portfolio (Name/ID)"
          />
        </Box>

        <Box mb={2}>
          <Typography variant="body2" gutterBottom>
            Select the destination portfolio (will receive all data from source):
          </Typography>
          <AsyncAutocomplete
            type="portfolio"
            onAddValue={(value) => {
              this.setState({ destinationPortfolio: value ? { id: value.id, label: value.label } : null });
            }}
            label="Destination Portfolio (Name/ID)"
          />
        </Box>

        {sourcePortfolio && destinationPortfolio && sourcePortfolio.id === destinationPortfolio.id && (
          <Box className={classes.errorBox}>
            <Typography variant="body2" color="error">
              Source and destination portfolios must be different.
            </Typography>
          </Box>
        )}

        <Box className={classes.warningBox}>
          <Typography variant="body2" color="textPrimary">
            <strong>Warning:</strong> This action cannot be undone. All data from the source
            portfolio will be permanently moved to the destination portfolio. Properties from the
            source will be tagged with &apos;-m&apos; for identification. Make sure you have
            selected the correct portfolios before proceeding.
          </Typography>
        </Box>

        <Box mt={2}>
          <Checkbox
            checked={confirmed}
            onChange={() => this.setState({ confirmed: !confirmed })}
            label="I understand this action cannot be undone and want to proceed with the merge"
          />
        </Box>

        {error && (
          <Box className={classes.errorBox}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  render() {
    const {
      sourcePortfolio,
      destinationPortfolio,
      confirmed,
      loading,
      success,
    } = this.state;
    const { isOpen } = this.props;

    const canMerge = sourcePortfolio
      && destinationPortfolio
      && sourcePortfolio.id !== destinationPortfolio.id
      && confirmed
      && !loading;

    return (
      <Dialog
        open={isOpen}
        onClose={this.handleClose}
        aria-labelledby="merge-portfolio-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="merge-portfolio-dialog-title">
          {success ? 'Merge Complete' : 'Merge Portfolios'}
        </DialogTitle>
        <DialogContent>
          {success ? this.renderSuccessView() : this.renderMergeForm()}
        </DialogContent>
        <DialogActions>
          {success ? (
            <Button onClick={this.handleClose} color="primary" variant="contained" disableElevation>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={this.handleClose} color="primary" disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={this.handleMerge}
                color="primary"
                variant="contained"
                disableElevation
                disabled={!canMerge}
              >
                {loading ? <CircularProgress size={24} /> : 'Merge'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

MergePortfolioDialog.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeDialog: PropTypes.func.isRequired,
};

export default withStyles(styles)(MergePortfolioDialog);
